import { useEffect, useRef } from "react";

function rr(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function readingScrim(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  dark: boolean
) {
  const [br, bg, bb] = dark ? [5, 5, 5] : [255, 255, 255];
  const cx = cw / 2,
    cy = ch / 2;
  const rad = Math.min(cw, ch) * 0.62;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
  g.addColorStop(0, `rgba(${br},${bg},${bb},1)`);
  g.addColorStop(0.42, `rgba(${br},${bg},${bb},0.92)`);
  g.addColorStop(0.7, `rgba(${br},${bg},${bb},0.45)`);
  g.addColorStop(1, `rgba(${br},${bg},${bb},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, cw, ch);
}

const APPTS = [
  { d: 0, s: 1, l: 2 },
  { d: 0, s: 6, l: 1 },
  { d: 1, s: 3, l: 3 },
  { d: 2, s: 0, l: 2 },
  { d: 2, s: 5, l: 2 },
  { d: 3, s: 2, l: 1 },
  { d: 3, s: 7, l: 2 },
  { d: 4, s: 1, l: 2 },
  { d: 4, s: 5, l: 3 },
  { d: 5, s: 4, l: 2 },
  { d: 6, s: 0, l: 1 },
  { d: 6, s: 6, l: 3 },
];

export function ErrorCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    let raf: number;
    const dpr = window.devicePixelRatio || 1;

    function resize() {
      const w = canvas!.offsetWidth,
        h = canvas!.offsetHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const DAYS = 7,
      SLOTS = 10;
    let frame = 0,
      glitch = 0,
      next = 90;

    function draw() {
      frame++;
      const dark = document.documentElement.classList.contains("dark");
      const cw = canvas!.offsetWidth,
        ch = canvas!.offsetHeight;
      const [br, bg, bb] = dark ? [5, 5, 5] : [255, 255, 255];
      const [fr, fg, fb] = dark ? [245, 245, 245] : [10, 10, 10];
      ctx.fillStyle = `rgb(${br},${bg},${bb})`;
      ctx.fillRect(0, 0, cw, ch);
      const gw = Math.min(cw * 0.7, 720),
        gh = Math.min(ch * 0.74, 580);
      const ox = (cw - gw) / 2,
        oy = (ch - gh) / 2;
      const colW = gw / DAYS,
        rowH = gh / SLOTS;
      if (--next <= 0) {
        glitch = 14;
        next = 70 + Math.floor(Math.random() * 90);
      }
      const g = glitch > 0 ? glitch / 14 : 0;
      if (glitch > 0) glitch--;
      ctx.strokeStyle = `rgba(${fr},${fg},${fb},${dark ? 0.1 : 0.07})`;
      ctx.lineWidth = 1;
      for (let c = 0; c <= DAYS; c++) {
        ctx.beginPath();
        ctx.moveTo(ox + c * colW, oy);
        ctx.lineTo(ox + c * colW, oy + gh);
        ctx.stroke();
      }
      for (let r = 0; r <= SLOTS; r++) {
        ctx.beginPath();
        ctx.moveTo(ox, oy + r * rowH);
        ctx.lineTo(ox + gw, oy + r * rowH);
        ctx.stroke();
      }
      APPTS.forEach((a, i) => {
        const jit = g > 0 ? Math.sin(i * 9.7) * g * colW * 0.9 : 0;
        const tear = g > 0 && i % 3 === 0;
        const x = ox + a.d * colW + colW * 0.14 + jit,
          y = oy + a.s * rowH + rowH * 0.12;
        const w = colW * 0.72,
          h = a.l * rowH - rowH * 0.24;
        if (tear) {
          rr(ctx, x - colW * 0.5, y, w, h, 4);
          ctx.fillStyle = `rgba(${fr},${fg},${fb},${0.12 * g})`;
          ctx.fill();
        }
        rr(ctx, x, y, w, h, 4);
        ctx.fillStyle = `rgba(${fr},${fg},${fb},${(dark ? 0.5 : 0.4) * (g > 0 ? 0.4 + Math.abs(Math.sin(frame * 0.6 + i)) * 0.6 : 1)})`;
        ctx.fill();
      });
      readingScrim(ctx, cw, ch, dark);
      raf = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
}
